/**
 * E-commerce Board Sync Service
 * Manages bidirectional synchronization between e-commerce entities and board items
 */
import { supabase } from '@/lib/supabase-client';
// ============================================
// BOARD TEMPLATES
// ============================================
export const BOARD_TEMPLATES = {
    order: {
        name: 'Orders Board',
        description: 'Manage orders with kanban workflow',
        columns: [
            { id: 'order-number', name: 'Order #', type: 'text', width: 150 },
            { id: 'customer', name: 'Customer', type: 'person', width: 200 },
            { id: 'total', name: 'Total', type: 'numbers', width: 120 },
            { id: 'status', name: 'Status', type: 'status', width: 150 },
            { id: 'payment-status', name: 'Payment', type: 'status', width: 150 },
            { id: 'fulfillment', name: 'Fulfillment', type: 'status', width: 150 },
            { id: 'date', name: 'Date', type: 'date', width: 150 },
            { id: 'tracking', name: 'Tracking', type: 'text', width: 150 },
        ],
        groups: [
            { title: 'Pending', color: '#F59E0B', position: 0 },
            { title: 'Confirmed', color: '#3B82F6', position: 1 },
            { title: 'Processing', color: '#8B5CF6', position: 2 },
            { title: 'Shipped', color: '#10B981', position: 3 },
            { title: 'Delivered', color: '#06B6D4', position: 4 },
        ],
        viewType: 'kanban',
    },
    product: {
        name: 'Products Board',
        description: 'Manage product inventory and details',
        columns: [
            { id: 'product-name', name: 'Product', type: 'text', width: 250 },
            { id: 'sku', name: 'SKU', type: 'text', width: 120 },
            { id: 'price', name: 'Price', type: 'numbers', width: 100 },
            { id: 'stock', name: 'Stock', type: 'numbers', width: 100 },
            { id: 'collection', name: 'Collection', type: 'status', width: 150 },
            { id: 'status', name: 'Status', type: 'status', width: 120 },
            { id: 'updated', name: 'Updated', type: 'date', width: 150 },
        ],
        groups: [
            { title: 'Active', color: '#10B981', position: 0 },
            { title: 'Low Stock', color: '#F59E0B', position: 1 },
            { title: 'Out of Stock', color: '#EF4444', position: 2 },
            { title: 'Archived', color: '#64748B', position: 3 },
        ],
        viewType: 'table',
    },
    customer: {
        name: 'Customers Board',
        description: 'Track customer lifecycle and engagement',
        columns: [
            { id: 'name', name: 'Name', type: 'text', width: 200 },
            { id: 'email', name: 'Email', type: 'text', width: 220 },
            { id: 'lifetime-value', name: 'LTV', type: 'numbers', width: 120 },
            { id: 'total-orders', name: 'Orders', type: 'numbers', width: 100 },
            { id: 'segment', name: 'Segment', type: 'status', width: 150 },
            { id: 'last-order', name: 'Last Order', type: 'date', width: 150 },
        ],
        groups: [
            { title: 'New', color: '#3B82F6', position: 0 },
            { title: 'Regular', color: '#10B981', position: 1 },
            { title: 'VIP', color: '#8B5CF6', position: 2 },
            { title: 'At Risk', color: '#F59E0B', position: 3 },
            { title: 'Lost', color: '#EF4444', position: 4 },
        ],
        viewType: 'table',
    },
    webinar: {
        name: 'Webinars Board',
        description: 'Schedule and manage webinar events',
        columns: [
            { id: 'title', name: 'Title', type: 'text', width: 250 },
            { id: 'date', name: 'Date', type: 'date', width: 150 },
            { id: 'instructor', name: 'Instructor', type: 'person', width: 180 },
            { id: 'level', name: 'Level', type: 'status', width: 120 },
            { id: 'registrations', name: 'Registered', type: 'numbers', width: 120 },
            { id: 'capacity', name: 'Capacity', type: 'numbers', width: 100 },
            { id: 'status', name: 'Status', type: 'status', width: 150 },
        ],
        groups: [
            { title: 'Draft', color: '#64748B', position: 0 },
            { title: 'Scheduled', color: '#3B82F6', position: 1 },
            { title: 'In Progress', color: '#F59E0B', position: 2 },
            { title: 'Completed', color: '#10B981', position: 3 },
            { title: 'Cancelled', color: '#EF4444', position: 4 },
        ],
        viewType: 'calendar',
    },
};
// ============================================
// DEFAULT FIELD MAPPINGS
// ============================================
const DEFAULT_FIELD_MAPPINGS = {
    order: [
        {
            boardColumn: 'order-number',
            ecommerceField: 'orderNumber',
            syncDirection: 'to_board',
        },
        {
            boardColumn: 'customer',
            ecommerceField: 'customer',
            syncDirection: 'to_board',
        },
        {
            boardColumn: 'total',
            ecommerceField: 'total',
            syncDirection: 'to_board',
        },
        {
            boardColumn: 'status',
            ecommerceField: 'status',
            syncDirection: 'bidirectional',
        },
        {
            boardColumn: 'payment-status',
            ecommerceField: 'paymentStatus',
            syncDirection: 'to_board',
        },
        {
            boardColumn: 'fulfillment',
            ecommerceField: 'fulfillmentStatus',
            syncDirection: 'bidirectional',
        },
        {
            boardColumn: 'date',
            ecommerceField: 'createdAt',
            syncDirection: 'to_board',
        },
        {
            boardColumn: 'tracking',
            ecommerceField: 'trackingNumber',
            syncDirection: 'bidirectional',
        },
    ],
    product: [
        {
            boardColumn: 'product-name',
            ecommerceField: 'name',
            syncDirection: 'to_board',
        },
        {
            boardColumn: 'sku',
            ecommerceField: 'sku',
            syncDirection: 'to_board',
        },
        {
            boardColumn: 'price',
            ecommerceField: 'price',
            syncDirection: 'to_board',
        },
        {
            boardColumn: 'stock',
            ecommerceField: 'stock',
            syncDirection: 'bidirectional',
        },
        {
            boardColumn: 'collection',
            ecommerceField: 'collectionId',
            syncDirection: 'to_board',
        },
        {
            boardColumn: 'status',
            ecommerceField: 'isActive',
            syncDirection: 'bidirectional',
        },
    ],
    customer: [
        {
            boardColumn: 'name',
            ecommerceField: 'firstName',
            syncDirection: 'to_board',
        },
        {
            boardColumn: 'email',
            ecommerceField: 'email',
            syncDirection: 'to_board',
        },
        {
            boardColumn: 'lifetime-value',
            ecommerceField: 'lifetimeValue',
            syncDirection: 'to_board',
        },
        {
            boardColumn: 'total-orders',
            ecommerceField: 'totalOrders',
            syncDirection: 'to_board',
        },
        {
            boardColumn: 'segment',
            ecommerceField: 'segment',
            syncDirection: 'bidirectional',
        },
        {
            boardColumn: 'last-order',
            ecommerceField: 'lastOrderAt',
            syncDirection: 'to_board',
        },
    ],
    webinar: [
        {
            boardColumn: 'title',
            ecommerceField: 'title',
            syncDirection: 'to_board',
        },
        {
            boardColumn: 'date',
            ecommerceField: 'date',
            syncDirection: 'to_board',
        },
        {
            boardColumn: 'instructor',
            ecommerceField: 'instructorName',
            syncDirection: 'to_board',
        },
        {
            boardColumn: 'level',
            ecommerceField: 'level',
            syncDirection: 'to_board',
        },
        {
            boardColumn: 'registrations',
            ecommerceField: 'currentRegistrations',
            syncDirection: 'to_board',
        },
        {
            boardColumn: 'capacity',
            ecommerceField: 'maxCapacity',
            syncDirection: 'to_board',
        },
        {
            boardColumn: 'status',
            ecommerceField: 'status',
            syncDirection: 'bidirectional',
        },
    ],
    discount: [],
    collection: [],
};
// ============================================
// BOARD SYNC SERVICE
// ============================================
export const EcommerceBoardSyncService = {
    // Get sync record by entity
    async getSyncRecord(entityType, entityId) {
        const { data, error } = await supabase
            .from('ecommerce_board_sync')
            .select('*')
            .eq('entity_type', entityType)
            .eq('entity_id', entityId)
            .single();
        if (error && error.code !== 'PGRST116') {
            throw error;
        }
        return data ? data : null;
    },
    // Get sync record by board item
    async getSyncRecordByBoardItem(boardItemId) {
        const { data, error } = await supabase
            .from('ecommerce_board_sync')
            .select('*')
            .eq('board_item_id', boardItemId)
            .single();
        if (error && error.code !== 'PGRST116') {
            throw error;
        }
        return data ? data : null;
    },
    // Create sync record
    async createSyncRecord(storeId, entityType, entityId, boardId, boardItemId, syncDirection = 'bidirectional') {
        const { data, error } = await supabase
            .from('ecommerce_board_sync')
            .insert({
            store_id: storeId,
            entity_type: entityType,
            entity_id: entityId,
            board_id: boardId,
            board_item_id: boardItemId,
            sync_direction: syncDirection,
            field_mappings: DEFAULT_FIELD_MAPPINGS[entityType],
            sync_status: 'active',
        })
            .select()
            .single();
        if (error)
            throw error;
        return data;
    },
    // Update sync record
    async updateSyncRecord(syncId, updates) {
        const { data, error } = await supabase
            .from('ecommerce_board_sync')
            .update({
            sync_status: updates.sync_status,
            sync_error: updates.sync_error,
            last_synced_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        })
            .eq('id', syncId)
            .select()
            .single();
        if (error)
            throw error;
        return data;
    },
    // Delete sync record
    async deleteSyncRecord(syncId) {
        const { error } = await supabase
            .from('ecommerce_board_sync')
            .delete()
            .eq('id', syncId);
        if (error)
            throw error;
    },
    // Get all sync records for a store
    async getSyncRecordsForStore(storeId, entityType) {
        let query = supabase
            .from('ecommerce_board_sync')
            .select('*')
            .eq('store_id', storeId);
        if (entityType) {
            query = query.eq('entity_type', entityType);
        }
        const { data, error } = await query;
        if (error)
            throw error;
        return data || [];
    },
    // Enable sync for an entity
    async enableSync(storeId, entityType, entityId, boardId, groupId, boardItemId) {
        return this.createSyncRecord(storeId, entityType, entityId, boardId, boardItemId, 'bidirectional');
    },
    // Disable sync for an entity
    async disableSync(entityType, entityId) {
        const syncRecord = await this.getSyncRecord(entityType, entityId);
        if (syncRecord) {
            await this.deleteSyncRecord(syncRecord.id);
        }
    },
    // Pause sync temporarily
    async pauseSync(syncId) {
        return this.updateSyncRecord(syncId, { sync_status: 'paused' });
    },
    // Resume sync
    async resumeSync(syncId) {
        return this.updateSyncRecord(syncId, { sync_status: 'active' });
    },
    // Setup real-time subscriptions
    setupRealtimeSync(entityType, entityId, onUpdate) {
        const channel = supabase
            .channel(`${entityType}-${entityId}`)
            .on('postgres_changes', {
            event: 'UPDATE',
            schema: 'public',
            table: `ecommerce_${entityType}s`,
            filter: `id=eq.${entityId}`,
        }, (payload) => {
            onUpdate(payload.new);
        })
            .subscribe();
        return channel;
    },
    // Get field mappings
    getFieldMappings(entityType) {
        return DEFAULT_FIELD_MAPPINGS[entityType] || [];
    },
    // Get board template
    getBoardTemplate(entityType) {
        return BOARD_TEMPLATES[entityType];
    },
};
